import React, { useEffect, useState } from "react";
import { Settings, Save, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Button, Field, Input, Badge } from "@/components/ui-kit";
import { toast } from "sonner";

const CATS = ["cuti_tahunan", "cuti_khusus", "sakit", "izin"];
const LABELS = { cuti_tahunan: "Cuti Tahunan", cuti_khusus: "Cuti Khusus", sakit: "Sakit", izin: "Izin" };

/** Iter v34e (#1) — Admin manage cuti quota per karyawan per tahun. */
export default function CutiQuotas() {
  const [data, setData] = useState({ rows: [], default_quota: {} });
  const [year, setYear] = useState(new Date().getFullYear());
  const [edits, setEdits] = useState({}); // {employee_id: {cat: total}}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  // Iter v34o (#6) — Filter departemen
  const [deptFilter, setDeptFilter] = useState("ALL");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leave-quotas?year=${year}`);
      setData(r.data);
      setEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat kuota");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year]);

  const setEdit = (eid, cat, val) => {
    setEdits((p) => ({ ...p, [eid]: { ...(p[eid] || {}), [cat]: val } }));
  };

  const save = async (row) => {
    const changes = edits[row.id] || {};
    if (Object.keys(changes).length === 0) return;
    setSaving((p) => ({ ...p, [row.id]: true }));
    try {
      const payload = { year, ...Object.fromEntries(Object.entries(changes).map(([k, v]) => [k, Number(v)])) };
      await api.patch(`/leave-quotas/${row.id}`, payload);
      toast.success(`Kuota ${row.name} diperbarui`);
      setEdits((p) => { const cp = { ...p }; delete cp[row.id]; return cp; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal simpan kuota");
    } finally { setSaving((p) => ({ ...p, [row.id]: false })); }
  };

  return (
    <div className="space-y-6" data-testid="cuti-quotas-page">
      <PageHeader overline="HR" title="Manage Kuota Cuti Karyawan"
        description="Atur jatah cuti tahunan, khusus, sakit, dan izin untuk setiap karyawan per tahun." />

      <Card className="p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <Field label="Tahun" className="!mb-0">
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || new Date().getFullYear())} className="w-32" />
          </Field>
          <Field label="Departemen" className="!mb-0">
            <select
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              className="border border-stone-300 px-2 py-1.5 text-xs bg-white min-w-[160px]"
              data-testid="quota-dept-filter"
            >
              <option value="ALL">Semua Departemen</option>
              {Array.from(new Set((data.rows || []).map((r) => r.department_category).filter(Boolean))).sort().map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
              {(data.rows || []).some((r) => !r.department_category) && (
                <option value="__none__">(Tanpa Departemen)</option>
              )}
            </select>
          </Field>
        </div>
        <div className="text-[11px] text-stone-500">
          Default kuota: {CATS.map((k) => <span key={k}><b>{LABELS[k]}</b> {data.default_quota?.[k] ?? 0}h</span>).reduce((a, b, i) => i ? [...a, " · ", b] : [b], [])}
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </Card>

      <Card className="p-0 overflow-x-auto" data-testid="cuti-quotas-table">
        <table className="w-full text-xs">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-600">
            <tr>
              <th className="text-left px-3 py-2.5">Karyawan</th>
              <th className="text-left px-3 py-2.5">Departemen</th>
              {CATS.map((c) => (
                <th key={c} className="text-center px-3 py-2.5">{LABELS[c]}<br/><span className="text-[9px] font-normal">total / terpakai</span></th>
              ))}
              <th className="text-center px-3 py-2.5">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading && <tr><td colSpan={7} className="px-3 py-12 text-center text-stone-400">Memuat...</td></tr>}
            {!loading && data.rows.length === 0 && <tr><td colSpan={7} className="px-3 py-12 text-center text-stone-400">Belum ada karyawan aktif.</td></tr>}
            {data.rows
              .filter((r) => {
                if (deptFilter === "ALL") return true;
                if (deptFilter === "__none__") return !r.department_category;
                return r.department_category === deptFilter;
              })
              .map((r) => (
              <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`quota-row-${r.id}`}>
                <td className="px-3 py-2.5">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[10px] font-mono text-stone-500">{r.employee_code}</div>
                </td>
                <td className="px-3 py-2.5 text-stone-700">{r.department_category || "—"}</td>
                {CATS.map((c) => {
                  const q = r.quotas[c] || { total: 0, used: 0 };
                  const editVal = edits[r.id]?.[c];
                  const display = editVal !== undefined ? editVal : q.total;
                  return (
                    <td key={c} className="px-3 py-2.5 text-center">
                      <input
                        type="number"
                        min={0}
                        value={display}
                        onChange={(e) => setEdit(r.id, c, e.target.value)}
                        className={`w-16 border ${editVal !== undefined ? "border-amber-400 bg-amber-50" : "border-stone-300"} px-2 py-1 text-xs text-center font-mono`}
                        data-testid={`quota-input-${r.id}-${c}`}
                      />
                      <div className="text-[10px] text-stone-500 mt-0.5">{q.used}h dipakai</div>
                    </td>
                  );
                })}
                <td className="px-3 py-2.5 text-center">
                  {edits[r.id] && Object.keys(edits[r.id]).length > 0 && (
                    <Button variant="primary" onClick={() => save(r)} disabled={saving[r.id]} data-testid={`quota-save-${r.id}`}>
                      <Save className="h-3 w-3" /> {saving[r.id] ? "..." : "Simpan"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
